import { ArrowUpRight, Loader2 } from 'lucide-react';
import { useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from '@/components/ui';
import { useCreateUpgradeOrder, useUpgradeOptions } from '@/hooks/useOrders';
import { errorMessage } from '@/utils/apiError';
import cn from '@/utils/cn';

const fmtDate = (iso: string | null): string => {
  if (!iso) return '-';
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

interface UpgradePlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: number;
  clientName: string;
  /** Called with the new order once it has been opened. */
  onCreated: (orderId: number, orderNumber: number) => void;
}

/**
 * Picks the higher tier to move a client's live MFBOX plan to, then opens the
 * Upgrade order. The price is deliberately NOT shown here - it is prorated
 * server-side from the plan being replaced and appears in the wizard's
 * quotation, which is the one place pricing is stated.
 */
export const UpgradePlanDialog = ({
  open,
  onOpenChange,
  leadId,
  clientName,
  onCreated,
}: UpgradePlanDialogProps) => {
  const { toast } = useToast();
  const options = useUpgradeOptions(leadId, open);
  const create = useCreateUpgradeOrder();
  const [selected, setSelected] = useState<string | null>(null);

  const current = options.data?.current ?? null;
  const targets = options.data?.targets ?? [];

  const submit = () => {
    if (!selected) {
      toast.error('Choose the plan to upgrade to');
      return;
    }
    create.mutate(
      { leadId, targetProductCode: selected },
      {
        onSuccess: (order) => {
          toast.success(`Upgrade order #${order.orderNumber} started for ${clientName}`);
          setSelected(null);
          onOpenChange(false);
          onCreated(order.id, order.orderNumber);
        },
        onError: (err) => toast.error(errorMessage(err, 'Could not start the upgrade')),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Upgrade plan</DialogTitle>
          <DialogDescription>
            {`Move ${clientName} to a higher MFBOX tier. They pay only the difference between the two plans - prorated to the months left in the period they have already paid for, and keeping their existing renewal date.`}
          </DialogDescription>
        </DialogHeader>

        {options.isLoading && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking what this client can upgrade to…
          </p>
        )}

        {options.isError && (
          <p className="text-sm text-destructive">
            {errorMessage(options.error, 'Could not load upgrade options.')}
          </p>
        )}

        {!options.isLoading && !options.isError && !current && (
          <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
            This client has no plan that can be upgraded. An upgrade needs a live AUM-based
            subscription whose service has been activated, on a product that has a tier set in Admin
            › Product Settings.
          </div>
        )}

        {current && (
          <>
            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Current plan
              </p>
              <p className="mt-0.5 text-sm font-semibold">
                {`${current.productName} · tier ${current.tier}`}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {current.activated
                  ? `Order #${current.orderNumber} · ${current.frequency} · renews ${fmtDate(current.dueDate)}`
                  : `Order #${current.orderNumber} · ${current.frequency} · awaiting service activation`}
              </p>
            </div>

            {/* Upgrading before the Service team has provisioned the plan is a
                different deal: nothing has been delivered, so the client pays
                the whole difference and the old plan is simply called off. */}
            {!current.activated && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-[11px] text-foreground">
                {`${current.productName} has not been activated by the Service team yet.`}
                {' Upgrading now charges the '}
                <span className="font-semibold">full difference</span>
                {' in setup price - there is nothing used up to prorate - and once this'}
                {` upgrade is handed over to billing, ${current.productName} is withdrawn`}
                {' from the service queue so it is never provisioned.'}
              </div>
            )}

            {targets.length === 0 ? (
              <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
                {`${current.productName} is already the highest tier available. Add a higher-tier plan in Admin › Product Settings to offer an upgrade.`}
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm font-medium">Upgrade to</p>
                {targets.map((t) => {
                  const active = selected === t.productCode;
                  return (
                    <button
                      key={t.productCode}
                      type="button"
                      onClick={() => setSelected(t.productCode)}
                      aria-pressed={active}
                      className={cn(
                        'flex w-full items-center justify-between gap-3 rounded-lg border p-3 text-left transition',
                        active
                          ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                          : 'border-border hover:bg-muted/40',
                      )}
                    >
                      <span>
                        <span className="block text-sm font-semibold">{t.productName}</span>
                        <span className="block text-[11px] text-muted-foreground">
                          {`Tier ${t.tier} · ${t.productCode}`}
                        </span>
                      </span>
                      <ArrowUpRight
                        className={cn(
                          'h-4 w-4 shrink-0',
                          active ? 'text-primary' : 'text-muted-foreground',
                        )}
                      />
                    </button>
                  );
                })}
                <p className="text-[11px] text-muted-foreground">
                  {current.activated
                    ? `The exact amount is calculated on the quotation step, from this client's current AUM and the months left before ${fmtDate(current.dueDate)}.`
                    : "The exact amount is calculated on the quotation step, from this client's current AUM."}
                </p>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={create.isPending || !selected || targets.length === 0}>
            {create.isPending ? 'Starting…' : 'Start upgrade'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
